import React, { useEffect, useState } from 'react'
import { Permissions } from '../auth/rbac'
import { useAccess } from '../auth/useAccess'
import { api } from '../lib/api'
import { formatDateTimeMinute } from '../lib/date'
import { NoticeLogStatusLabel } from '../lib/labels'
import ListContainer from '../ui/ListContainer'
import { useToast } from '../ui/ToastProvider'

export default function NoticeLogsPage() {
  const { show } = useToast()
  const { guard, allow } = useAccess()
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [q, setQ] = useState('')
  const [channel, setChannel] = useState('ALL')
  const [status, setStatus] = useState('ALL')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [page, setPage] = useState(1)
  const [size, setSize] = useState(10)
  const [selectedLog, setSelectedLog] = useState(null)
  const [logDetail, setLogDetail] = useState(null)
  const [loadingDetail, setLoadingDetail] = useState(false)

  // 재발송 상태
  const [resendingLogs, setResendingLogs] = useState(new Set())
  const [selectedLogsForResend, setSelectedLogsForResend] = useState([])

  useEffect(() => {
    let ignore = false
    async function load() {
      setLoading(true)
      setError(null)
      try {
        const params = { page, size }
        if (q) params.q = q
        if (channel !== 'ALL') params.channel = channel
        if (status !== 'ALL') params.status = status
        if (from) params.from = from
        if (to) params.to = to
        const res = await api.get('/notice-logs', { params })
        const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
        if (!ignore) setItems(Array.isArray(payload) ? payload : payload?.items || [])
      } catch (e) {
        if (!ignore) setError('발송 로그를 불러오지 못했습니다')
      } finally {
        if (!ignore) setLoading(false)
      }
    }
    load()
    return () => { ignore = true }
  }, [q, channel, status, from, to, page, size])

  // 로그 상세 정보 로딩
  const loadLogDetail = async (logId) => {
    setLoadingDetail(true)
    try {
      const res = await api.get(`/notice-logs/${logId}`)
      const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
      setLogDetail(payload)
    } catch (err) {
      console.error('Failed to load log detail:', err)
    } finally {
      setLoadingDetail(false)
    }
  }

  // 로그 상세 모달 열기
  const openLogDetail = (log) => {
    setSelectedLog(log)
    loadLogDetail(log.id)
  }

  // 개별 재발송
  const resendSingleLog = async (logId) => {
    if (!allow.perm([Permissions.NOTICE_SEND])) {
      show('권한이 없습니다', 'error')
      return
    }
    setResendingLogs(prev => new Set([...prev, logId]))
    try {
      await api.post(`/notice-logs/${logId}/resend`)
      show('재발송 요청 완료', 'success')

      // 목록 새로고침
      const params = { page, size }
      if (q) params.q = q
      if (channel !== 'ALL') params.channel = channel
      if (status !== 'ALL') params.status = status
      if (from) params.from = from
      if (to) params.to = to
      const res = await api.get('/notice-logs', { params })
      const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
      setItems(Array.isArray(payload) ? payload : payload?.items || [])
    } catch (err) {
      show('재발송 실패', 'error')
    } finally {
      setResendingLogs(prev => {
        const newSet = new Set(prev)
        newSet.delete(logId)
        return newSet
      })
    }
  }

  // 선택된 로그들 일괄 재발송
  const resendSelectedLogs = async () => {
    if (!allow.perm([Permissions.NOTICE_SEND])) {
      show('권한이 없습니다', 'error')
      return
    }
    if (selectedLogsForResend.length === 0) return

    const failedLogIds = selectedLogsForResend.map(log => log.id)
    setResendingLogs(prev => new Set([...prev, ...failedLogIds]))

    try {
      await api.post('/notice-logs/bulk-resend', {
        logIds: failedLogIds
      })
      show(`${failedLogIds.length}건 재발송 요청 완료`, 'success')
      setSelectedLogsForResend([])

      // 목록 새로고침
      const params = { page, size }
      if (q) params.q = q
      if (channel !== 'ALL') params.channel = channel
      if (status !== 'ALL') params.status = status
      if (from) params.from = from
      if (to) params.to = to
      const res = await api.get('/notice-logs', { params })
      const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
      setItems(Array.isArray(payload) ? payload : payload?.items || [])
    } catch (err) {
      show('일괄 재발송 실패', 'error')
    } finally {
      setResendingLogs(prev => {
        const newSet = new Set(prev)
        failedLogIds.forEach(id => newSet.delete(id))
        return newSet
      })
    }
  }

  // 체크박스 토글
  const toggleLogSelection = (log) => {
    setSelectedLogsForResend(prev => {
      const exists = prev.find(l => l.id === log.id)
      if (exists) {
        return prev.filter(l => l.id !== log.id)
      } else {
        return [...prev, log]
      }
    })
  }

  // 모든 실패 로그 선택
  const selectAllFailedLogs = () => {
    const failedLogs = items.filter(item => item.status === 'FAILED')
    setSelectedLogsForResend(failedLogs)
  }

  // 선택 해제
  const clearSelection = () => {
    setSelectedLogsForResend([])
  }

  // 상태별 스타일
  const getStatusStyle = (status) => {
    switch (status) {
      case 'SENT':
        return 'bg-green-100 text-green-800'
      case 'FAILED':
        return 'bg-red-100 text-red-800'
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const header = (
    selectedLogsForResend.length > 0 ? (
      <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded flex items-center justify-between">
        <div className="text-sm text-blue-800">
          선택된 실패 로그: {selectedLogsForResend.length}건
        </div>
        <div className="flex gap-2">
          <button className="text-sm text-blue-600 hover:underline" onClick={clearSelection}>선택 해제</button>
          <button {...guard.disableIfNoPerm([Permissions.NOTICE_SEND])} className="rounded bg-blue-600 text-white px-3 py-1 text-sm" onClick={resendSelectedLogs}>
            {resendingLogs.size > 0 ? '재발송 중...' : '선택 재발송'}
          </button>
        </div>
      </div>
    ) : null
  )

  const table = (
    <table className="min-w-full text-sm">
      <thead className="bg-slate-50 text-slate-600">
        <tr>
          <th className="px-3 py-2 text-left">
            <input
              type="checkbox"
              onChange={(e) => {
                if (e.target.checked) {
                  selectAllFailedLogs()
                } else {
                  clearSelection()
                }
              }}
              checked={selectedLogsForResend.length === items.filter(item => item.status === 'FAILED').length && items.filter(item => item.status === 'FAILED').length > 0}
            />
          </th>
          <th className="px-3 py-2 text-left">ID</th>
          <th className="px-3 py-2 text-left">템플릿/제목</th>
          <th className="px-3 py-2 text-left">채널</th>
          <th className="px-3 py-2 text-left">상태</th>
          <th className="px-3 py-2 text-left">일시</th>
          <th className="px-3 py-2 text-left">액션</th>
        </tr>
      </thead>
      <tbody>
        {items.map(l => (
          <tr key={l.id} className="border-t hover:bg-slate-50">
            <td className="px-3 py-2">
              {l.status === 'FAILED' && (
                <input
                  type="checkbox"
                  checked={selectedLogsForResend.find(log => log.id === l.id) !== undefined}
                  onChange={() => toggleLogSelection(l)}
                />
              )}
            </td>
            <td className="px-3 py-2">{l.id}</td>
            <td className="px-3 py-2">{l.title || l.templateName}</td>
            <td className="px-3 py-2">{l.channel}</td>
            <td className="px-3 py-2">
              <span className={`px-2 py-1 rounded text-xs ${getStatusStyle(l.status)}`}>
                {NoticeLogStatusLabel[l.status] || l.status}
              </span>
            </td>
            <td className="px-3 py-2">{formatDateTimeMinute(l.createdAt)}</td>
            <td className="px-3 py-2">
              <div className="flex gap-2">
                <button className="text-brand-navy hover:underline text-xs" onClick={() => openLogDetail(l)}>상세보기</button>
                {l.status === 'FAILED' && (
                  <button {...guard.disableIfNoPerm([Permissions.NOTICE_SEND])} className="text-red-600 hover:underline text-xs disabled:opacity-50" onClick={() => resendSingleLog(l.id)} disabled={resendingLogs.has(l.id)}>
                    {resendingLogs.has(l.id) ? '재발송 중...' : '재발송'}
                  </button>
                )}
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )

  const footer = (
    <div className="flex items-center justify-between p-3 border-t text-sm">
      <div className="flex items-center gap-2">
        <select className="rounded border px-2 py-1" value={size} onChange={(e) => setSize(Number(e.target.value))}>
          <option value={10}>10개</option>
          <option value={20}>20개</option>
          <option value={50}>50개</option>
        </select>
      </div>
      <div className="flex items-center gap-2">
        <button className="rounded border px-3 py-1 disabled:opacity-50" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>이전</button>
        <span>페이지 {page}</span>
        <button className="rounded border px-3 py-1" onClick={() => setPage((p) => p + 1)}>다음</button>
      </div>
    </div>
  )

  return (
    <section>
      <h1 className="text-2xl font-bold">공지 발송 로그</h1>
      <div className="mt-4 flex flex-wrap gap-2 items-center">
        <input className="rounded border px-3 py-2 text-sm" placeholder="검색: 제목/템플릿" value={q} onChange={(e) => setQ(e.target.value)} />
        <select className="rounded border px-3 py-2 text-sm" value={channel} onChange={(e) => setChannel(e.target.value)}>
          <option value="ALL">채널 전체</option>
          <option value="ALIMTALK">ALIMTALK</option>
          <option value="SMS">SMS</option>
          <option value="FCM">FCM</option>
        </select>
        <select className="rounded border px-3 py-2 text-sm" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="ALL">상태 전체</option>
          <option value="SENT">SENT</option>
          <option value="FAILED">FAILED</option>
          <option value="PENDING">PENDING</option>
        </select>
        <input className="rounded border px-3 py-2 text-sm" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        <input className="rounded border px-3 py-2 text-sm" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
      </div>

      <ListContainer
        loading={loading}
        error={error}
        onRetry={() => {
          // 단순 재시도: 현재 필터로 재조회
          (async () => {
            try {
              const params = { page, size }
              if (q) params.q = q
              if (channel !== 'ALL') params.channel = channel
              if (status !== 'ALL') params.status = status
              if (from) params.from = from
              if (to) params.to = to
              const res = await api.get('/notice-logs', { params })
              setItems(Array.isArray(res.data) ? res.data : res.data.items || [])
              setError(null)
            } catch (e) {
              setError('발송 로그를 불러오지 못했습니다')
            }
          })()
        }}
        header={header}
        table={table}
        footer={footer}
      />

      {/* 로그 상세 모달 */}
      {selectedLog && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center p-4" onClick={() => setSelectedLog(null)}>
          <div className="w-full max-w-2xl rounded bg-white p-6 max-h-[80vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">발송 로그 상세</h3>
              <button className="text-slate-500 hover:text-slate-700" onClick={() => setSelectedLog(null)}>✕</button>
            </div>

            {loadingDetail ? (
              <div className="py-8 text-center">로딩 중…</div>
            ) : logDetail ? (
              <div className="space-y-4 text-sm">
                {/* 기본 정보 */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-slate-600 font-medium">로그 ID</label>
                    <div className="mt-1">{logDetail.id}</div>
                  </div>
                  <div>
                    <label className="block text-slate-600 font-medium">상태</label>
                    <div className="mt-1">
                      <span className={`px-2 py-1 rounded text-xs ${getStatusStyle(logDetail.status)}`}>
                        {NoticeLogStatusLabel[logDetail.status] || logDetail.status}
                      </span>
                    </div>
                  </div>
                  <div>
                    <label className="block text-slate-600 font-medium">채널</label>
                    <div className="mt-1">{logDetail.channel}</div>
                  </div>
                  <div>
                    <label className="block text-slate-600 font-medium">발송 일시</label>
                    <div className="mt-1">{logDetail.createdAt}</div>
                  </div>
                </div>

                {/* 발송 내용 */}
                <div>
                  <label className="block text-slate-600 font-medium">제목</label>
                  <div className="mt-1 p-2 bg-slate-50 rounded">{logDetail.title}</div>
                </div>

                <div>
                  <label className="block text-slate-600 font-medium">내용</label>
                  <div className="mt-1 p-2 bg-slate-50 rounded whitespace.pre-wrap max-h-32 overflow-auto">
                    {logDetail.content}
                  </div>
                </div>

                {/* 수신자 정보 */}
                <div>
                  <label className="block text-slate-600 font-medium">수신자</label>
                  <div className="mt-1 p-2 bg-slate-50 rounded">
                    <div>이름: {logDetail.recipientName || '-'}</div>
                    <div>연락처: {logDetail.recipientPhone || '-'}</div>
                    <div>이메일: {logDetail.recipientEmail || '-'}</div>
                  </div>
                </div>

                {/* 실패 정보 */}
                {logDetail.status === 'FAILED' && logDetail.errorDetails && (
                  <div>
                    <label className="block text-slate-600 font-medium text-red-600">실패 사유</label>
                    <div className="mt-1 p-2 bg-red-50 border border-red-200 rounded">
                      <div className="text-red-800">
                        <div className="font-medium mb-1">에러 코드: {logDetail.errorCode || '-'}</div>
                        <div className="mb-1">에러 메시지: {logDetail.errorMessage || '-'}</div>
                        {logDetail.errorDetails && (
                          <div>
                            <div className="font-medium mb-1">상세 정보:</div>
                            <pre className="text-xs whitespace-pre-wrap bg-red-100 p-2 rounded">
                              {JSON.stringify(logDetail.errorDetails, null, 2)}
                            </pre>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* 성공 정보 */}
                {logDetail.status === 'SENT' && logDetail.deliveryDetails && (
                  <div>
                    <label className="block text-slate-600 font-medium text-green-600">발송 성공 정보</label>
                    <div className="mt-1 p-2 bg-green-50 border border-green-200 rounded">
                      <div className="text-green-800">
                        <div>발송 ID: {logDetail.deliveryId || '-'}</div>
                        <div>발송 완료 시간: {logDetail.deliveredAt || '-'}</div>
                        {logDetail.deliveryDetails && (
                          <div className="mt-2">
                            <div className="font-medium mb-1">상세 정보:</div>
                            <pre className="text-xs whitespace-pre-wrap bg-green-100 p-2 rounded">
                              {JSON.stringify(logDetail.deliveryDetails, null, 2)}
                            </pre>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* 재시도 정보 */}
                {logDetail.retryCount > 0 && (
                  <div>
                    <label className="block text-slate-600 font-medium">재시도 정보</label>
                    <div className="mt-1 p-2 bg-yellow-50 border border-yellow-200 rounded">
                      <div>재시도 횟수: {logDetail.retryCount}</div>
                      <div>마지막 재시도: {logDetail.lastRetryAt || '-'}</div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="py-8 text-center text-sm text-slate-500">로그 상세 정보를 불러올 수 없습니다</div>
            )}

            <div className="mt-6 text-right">
              <button className="rounded border px-3 py-2 text-sm" onClick={() => setSelectedLog(null)}>닫기</button>
            </div>
          </div>
        </div>
      )}
    </section>
  )
}


